import { useProcessCoordination } from '@hooks/useProcessCoordination';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';
import { resetProcess, setVideoUrl } from 'src/store/slices/sentinel';
import { isValidVideoUrl } from 'src/utils/videoParser';
import { useAppDispatch, useAppSelector } from 'universal-scripts';

export function useSentinelGuard() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const intl = useIntl();
	const [searchParams] = useSearchParams();

	const status = useAppSelector((state) => state.sentinel.status);
	const videoUrl = useAppSelector((state) => state.sentinel.videoUrl);

	const urlParam = searchParams.get('url');
	const refreshParam = searchParams.get('refresh') === 'true';

	const confirmMessage = intl.formatMessage({ id: 'sentinel.process-view.active-process' });
	const invalidUrlMessage = intl.formatMessage({ id: 'sentinel.process-view.invalid-url' });
	const tabMessage = intl.formatMessage({ id: 'sentinel.process-view.processing-in-other-tab' });

	const { canStartProcess, otherTabVideoUrl } = useProcessCoordination();
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (urlParam) {
			if (!isValidVideoUrl(urlParam)) {
				alert(invalidUrlMessage);
				navigate('/sentinel', { replace: true });
				return;
			}
			if (videoUrl === urlParam) {
				return;
			}
			if (videoUrl) {
				if (status === 'processing') {
					const shouldSwitch = window.confirm(confirmMessage);
					if (!shouldSwitch) {
						navigate(`/sentinel?url=${encodeURIComponent(videoUrl)}`, { replace: true });
						return;
					}
				}
				dispatch(resetProcess());
			}
			dispatch(setVideoUrl({ url: urlParam, refreshCache: refreshParam }));
		} else {
			if (status === 'processing' && videoUrl) {
				// biome-ignore lint/suspicious/noConsole: navigation guard diagnostics until Sentry SDK is wired up
				console.warn('Redirecting back to active process');
				navigate(`/sentinel?url=${encodeURIComponent(videoUrl)}`, { replace: true });
				return;
			}
		}
	}, [urlParam, refreshParam, videoUrl, status, dispatch, navigate, confirmMessage, invalidUrlMessage]);

	useEffect(() => {
		if (canStartProcess === false && otherTabVideoUrl) {
			alert(tabMessage.replace('{url}', otherTabVideoUrl));
			dispatch(resetProcess());
			navigate('/sentinel', { replace: true });
		}
	}, [canStartProcess, otherTabVideoUrl, tabMessage, navigate, dispatch]);

	useEffect(() => {
		if (status !== 'processing') return;
		window.history.pushState(null, '', window.location.href);

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = '';
		};

		const handlePopState = () => {
			if (window.confirm(confirmMessage)) {
				dispatch(resetProcess());
				navigate('/sentinel', { replace: true });
			} else {
				window.history.pushState(null, '', window.location.href);
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			window.removeEventListener('popstate', handlePopState);
		};
	}, [status, dispatch, confirmMessage, navigate]);

	return { isReady: canStartProcess === true };
}
